import React from 'react';
import { User as UserIcon } from 'lucide-react';
import { Company } from '@/types';
import { formatDobInput, parseDobToDisplay } from '@/lib/formatters';
import { validateUkPhone } from '@/lib/validators';

interface CustomerDetails {
  name: string;
  email: string;
  phone: string;
  dob: string;
  jobTitle: string;
  poNumber?: string;
}

interface PatientFormProps {
  customerDetails: CustomerDetails;
  isCompanyBooking: boolean;
  isPhoneRequired: boolean;
  currentCompany: Company | null;
  showNameError: boolean;
  showDobError: boolean;
  showEmailError: boolean;
  showPhoneError: boolean;
  hasPhoneValue: boolean;
  onCustomerDetailsChange: (details: CustomerDetails) => void;
  onNameBlur: () => void;
  onDobBlur: () => void;
  onEmailBlur: () => void;
  onPhoneBlur: (normalizedPhone?: string) => void;
}

export const PatientForm: React.FC<PatientFormProps> = ({
  customerDetails,
  isCompanyBooking,
  isPhoneRequired,
  currentCompany,
  showNameError,
  showDobError,
  showEmailError,
  showPhoneError,
  hasPhoneValue,
  onCustomerDetailsChange,
  onNameBlur,
  onDobBlur,
  onEmailBlur,
  onPhoneBlur
}) => {
  const set = (field: keyof CustomerDetails) => (value: string) =>
    onCustomerDetailsChange({ ...customerDetails, [field]: value });

  return (
    <div className="bg-white p-6 border-2 border-slate-100 rounded-2xl shadow-sm">
      <h4 className="font-bold mb-4 text-slate-800 flex items-center gap-2">
        <UserIcon className="w-5 h-5 text-teal-600" /> Patient Information
      </h4>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 items-start">
          <div>
            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 min-h-[2rem]">
              Full Name (First name + Surname) *
            </label>
            <input
              required
              className="w-full border rounded-lg px-3 py-2 bg-slate-50 outline-none focus:ring-2 focus:ring-teal-500 text-sm font-medium"
              value={customerDetails.name}
              onChange={(e) => set('name')(e.target.value)}
              onBlur={onNameBlur}
            />
            {showNameError && (
              <p className="mt-1 text-xs font-semibold text-red-600">
                Please enter both a first name and a surname.
              </p>
            )}
          </div>
          <div>
            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 min-h-[2rem]">
              Date of Birth *
            </label>
            <input
              required
              type="text"
              inputMode="numeric"
              placeholder="DD-MM-YYYY"
              maxLength={10}
              className="w-full border rounded-lg px-3 py-2 bg-slate-50 outline-none focus:ring-2 focus:ring-teal-500 text-sm font-medium"
              value={customerDetails.dob}
              onChange={(e) => set('dob')(formatDobInput(e.target.value))}
              onBlur={(e) => {
                const parsed = parseDobToDisplay(e.target.value);
                if (parsed && parsed !== customerDetails.dob) {
                  set('dob')(parsed);
                }
                onDobBlur();
              }}
            />
            {showDobError && (
              <p className="mt-1 text-xs font-semibold text-red-600">
                Use DD-MM-YYYY. Year must be between 1900 and {new Date().getFullYear()}.
              </p>
            )}
          </div>
        </div>
        <div>
          <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">
            Email Address *
          </label>
          <input
            required
            type="email"
            className="w-full border rounded-lg px-3 py-2 bg-slate-50 outline-none focus:ring-2 focus:ring-teal-500 text-sm font-medium"
            value={customerDetails.email}
            onChange={(e) => set('email')(e.target.value)}
            onBlur={onEmailBlur}
          />
          {showEmailError && (
            <p className="mt-1 text-xs font-semibold text-red-600">Enter a valid email address.</p>
          )}
        </div>
        <div>
          <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">
            Mobile Number {isPhoneRequired ? '*' : '(optional)'}
          </label>
          <input
            type="tel"
            className="w-full border rounded-lg px-3 py-2 bg-slate-50 outline-none focus:ring-2 focus:ring-teal-500 text-sm font-medium"
            value={customerDetails.phone}
            onChange={(e) => set('phone')(e.target.value)}
            onBlur={(e) => {
              const value = e.target.value.trim();
              const result = value ? validateUkPhone(value) : null;
              onPhoneBlur(result?.valid ? result.normalized : undefined);
            }}
          />
          <p className="mt-1 text-[10px] text-slate-400">
            UK numbers only, e.g. 07123 456789 or +44 7123 456789.
          </p>
          {showPhoneError && (
            <p className="mt-1 text-xs font-semibold text-red-600">
              {isPhoneRequired && !hasPhoneValue
                ? 'Mobile number is required for individual bookings.'
                : 'Enter a valid UK phone number.'}
            </p>
          )}
        </div>
        <div>
          <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">
            Job Title (optional)
          </label>
          <input
            className="w-full border rounded-lg px-3 py-2 bg-slate-50 outline-none focus:ring-2 focus:ring-teal-500 text-sm font-medium"
            placeholder="e.g. Forklift Operator"
            value={customerDetails.jobTitle}
            onChange={(e) => set('jobTitle')(e.target.value)}
          />
          <p className="mt-1 text-[10px] text-slate-400">Printed on the medical certificate.</p>
        </div>
        {isCompanyBooking && (
          <div>
            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">
              PO Number {currentCompany?.requirePo && '*'}
            </label>
            <input
              required={currentCompany?.requirePo}
              className="w-full border rounded-lg px-3 py-2 bg-slate-50 outline-none focus:ring-2 focus:ring-teal-500 text-sm font-medium"
              value={customerDetails.poNumber ?? ''}
              onChange={(e) => set('poNumber')(e.target.value)}
            />
          </div>
        )}
      </div>
    </div>
  );
};
